import Loading from "@lhd/components/Loading";
import { ROUTES } from "@lhd/constants";
import { useGetAuth, useGetCompanyDetail, useGetPermissions } from "@lhd/hooks";
import { user } from "@lhd/features/users/selector";
import { Company } from "@lhd/types";
import { toString } from "lodash";
import { createContext, FC, ReactNode, useMemo } from "react";
import { useSelector } from "react-redux";
import { Navigate, useParams } from "react-router-dom";

interface Props {
  children: ReactNode;
  isConfiguration?: boolean;
}

const STATUS_ENUM = {
  LOADING: "loading",
  ERROR: "error",
  SUCCESS: "success",
};

export const CompanyHistoryContext = createContext<{
  data: Company | null;
  loading: boolean;
}>({
  data: null,
  loading: true,
});

const CompanyHistoryProtectedRoute: FC<Props> = ({
  children,
  isConfiguration,
}) => {
  const { id: companyId } = useParams();
  const { isUserResigned, auth } = useGetAuth();

  const {
    data: companyData,
    status,
    isError,
  } = useGetCompanyDetail({
    companyId: toString(companyId),
  });

  const permission = useGetPermissions();
  const statusAccepted = isConfiguration
    ? permission.isContractStatusAccepted
    : permission[ROUTES.COMPANIES];

  const permissionState = useMemo(() => {
    if (status === STATUS_ENUM.LOADING) {
      return {
        isLoading: true,
        data: null,
        shouldRedirect: !statusAccepted,
      };
    }

    if (status === STATUS_ENUM.ERROR || isError) {
      return {
        isLoading: false,
        data: null,
        shouldRedirect: true,
      };
    }

    if (status === STATUS_ENUM.SUCCESS) {
      if (!companyData?.data) {
        return {
          isLoading: false,
          data: null,
          shouldRedirect: true,
        };
      }
      return {
        isLoading: false,
        data: companyData.data as Company,
        shouldRedirect: !statusAccepted,
      };
    }

    return {
      isLoading: true,
      data: null,
      shouldRedirect: !statusAccepted,
    };
  }, [status, companyData, isError, statusAccepted]);

  if (permissionState.isLoading) {
    return <Loading />;
  }

  if (!auth) {
    return <Loading />;
  }

  if (permissionState.shouldRedirect || isUserResigned) {
    return <Navigate to={`/${ROUTES.NOT_FOUND_PAGE}`} replace />;
  }

  return (
    <CompanyHistoryContext.Provider
      value={{
        data: permissionState.data as Company,
        loading: permissionState.isLoading,
      }}
    >
      {children}
    </CompanyHistoryContext.Provider>
  );
};

export default CompanyHistoryProtectedRoute;
